import { get } from 'lodash';

import { secureLubaApi } from '../secureLubaApi';
import { HEADER_NAMES } from 'app/constants';

// Dedicated org-view endpoints. They hit the SAME /Application/search & /Permit/search URLs as the
// normal lists, but add the X-Org-View header so the backend applies the whitelisted org-subtree scope.
// The header is set per-endpoint here (never in the global prepareHeaders) and these endpoints use their
// own cache tags so org-scoped results never leak into the normal Application/Permit lists.
const ORG_VIEW_HEADER = { [HEADER_NAMES.ORG_VIEW]: 'true' };

export const orgViewApi = secureLubaApi.injectEndpoints({
  endpoints: (build) => ({
    searchOrgApplications: build.query({
      query: (params) => {
        const page = get(params, 'page', 1) - 1;
        const pageSize = get(params, 'pageSize', 25);
        return {
          url: `/application/search/${page}/${pageSize}`,
          headers: ORG_VIEW_HEADER,
        };
      },
      providesTags: [{ type: 'OrgApplication', id: 'LIST' }],
    }),
    searchOrgPermits: build.query({
      query: (params) => {
        const page = get(params, 'page', 1) - 1;
        const pageSize = get(params, 'pageSize', 25);
        return {
          url: `/permit/search/${page}/${pageSize}`,
          headers: ORG_VIEW_HEADER,
        };
      },
      providesTags: [{ type: 'OrgPermit', id: 'LIST' }],
    }),
    getOrgViewStatus: build.query({
      // Distinguishes "no Entra data synced yet" (hasOrgUsers=false) from a legitimately empty subtree.
      query: () => `/OrgView/status`,
      providesTags: [{ type: 'OrgStatus', id: 'CURRENT' }],
    }),
    searchOrgPeople: build.query({
      // The people in the caller's org scope, read from the Entra mirror rather than dbo.[User], so it
      // lists the whole organisation and not only those holding a LIS account.
      query: (params) => ({
        url: `/OrgView/people`,
        params: {
          search: get(params, 'search') || undefined,
          page: get(params, 'page', 1),
          pageSize: get(params, 'pageSize', 25),
        },
        headers: ORG_VIEW_HEADER,
      }),
      providesTags: [{ type: 'OrgPeople', id: 'LIST' }],
    }),
    getManagerChain: build.query({
      // The managers above one person plus their direct reports. Returns directory names, so the
      // backend serves it only to whitelisted callers who send the org-view header.
      query: (email) => ({
        url: `/OrgView/manager-chain`,
        params: { email },
        headers: ORG_VIEW_HEADER,
      }),
      providesTags: (result, error, email) => [{ type: 'OrgChain', id: email }],
    }),
  }),
});

export const {
  useSearchOrgApplicationsQuery,
  useSearchOrgPermitsQuery,
  useGetOrgViewStatusQuery,
  useSearchOrgPeopleQuery,
  useGetManagerChainQuery,
} = orgViewApi;
